import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Spinner, Button } from "@shopify/polaris";
import UserAvatar from "components/user-avatar/user-avatar";
import { useAppSelector } from "hooks";
import {
  setIsModalxOpen,
  setModalxComponent,
  setModalxComponentProps,
} from "components/modalx/modalx.slice";
import { AppDispatch } from "configureStore";
import {
  fetchPublicProfile,
  PublicProfile,
} from "services/user-public-profile.service";
import { startDm } from "services/chat.service";
import {
  setActiveTab,
  openConversation,
  upsertConversation,
} from "features/chat/chat.slice";
import { setExpanded } from "features/presence/presence.slice";
import useIsPhone from "features/files/use-is-phone";
import { formatLastSeen } from "features/presence/format-last-seen";
import { fetchUserProfile } from "services/points.service";
import { PointsUserProfile } from "types/Points";
import LevelBadge from "features/recognition/level-badge";
import { toast } from "sonner";

interface Props {
  companyId: string;
  userId: string;
}

const ONLINE_THRESHOLD_MS = 180_000;

const UserPublicProfileModal: React.FC<Props> = ({ companyId, userId }) => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isPhone = useIsPhone();
  const lang = ((i18n as any).language || "en") as "lt" | "en";
  const session = useAppSelector((state) => state.authReducer.session);
  const isSelf = session?.id != null && String(session.id) === String(userId);
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [points, setPoints] = useState<PointsUserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleOpenFullProfile = () => {
    dispatch(setIsModalxOpen(false));
    navigate(`/app/${companyId}/profile/${userId}/info`);
  };

  const [openingDm, setOpeningDm] = useState(false);

  const handleSendMessage = async () => {
    if (!companyId || !userId || isSelf) return;
    setOpeningDm(true);
    try {
      const convo = await startDm(companyId, userId);
      dispatch(upsertConversation(convo));
      dispatch(setActiveTab("chats"));
      dispatch(openConversation({ companyId, conversationId: convo.id }));
      dispatch(setExpanded(true));
      dispatch(setIsModalxOpen(false));
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t("error"));
    } finally {
      setOpeningDm(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPoints(null);
    (async () => {
      try {
        // Profile is required; recognition data is best-effort (auth covers
        // both, but we don't want a points hiccup to mask the rest of the modal).
        const [p, pts] = await Promise.all([
          fetchPublicProfile(companyId, userId),
          fetchUserProfile(companyId, userId).catch(() => null),
        ]);
        if (!cancelled) {
          setProfile(p);
          setPoints(pts);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.error || t("error"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, userId, t]);

  const handleGiveKudos = () => {
    if (!profile) return;
    dispatch(setModalxComponent("GiveKudosModal"));
    dispatch(setModalxComponentProps({
      companyId,
      toUser: {
        id: profile.id,
        name: profile.name,
        avatarFileId: profile.avatarFileId,
      },
    }));
  };

  const isOnline =
    profile?.lastSeenAt != null &&
    Date.now() - profile.lastSeenAt < ONLINE_THRESHOLD_MS;

  return (
    <div>
      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner size="large" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg">
          {error}
        </div>
      ) : profile ? (
        <div className="flex flex-col items-center gap-3 py-2">
          <UserAvatar
            companyId={companyId}
            user={{
              id: profile.id,
              name: profile.name,
              avatarFileId: profile.avatarFileId,
            }}
            size={160}
            rounded="lg"
            online={isOnline}
          />
          <div className="text-center">
            <div className="text-xl font-semibold">{profile.name}</div>
            {profile.position && (
              <div className="text-sm text-gray-600 mt-0.5">
                {profile.position}
              </div>
            )}
            <div
              className={`text-xs mt-1 ${
                isOnline ? "text-green-600" : "text-gray-400"
              }`}
            >
              {isOnline
                ? t("online")
                : profile.lastSeenAt
                ? `${t("last_seen")}: ${formatLastSeen(profile.lastSeenAt, t, lang)}`
                : t("last_seen_never")}
            </div>
          </div>

          {points && (
            <div className="w-full mt-2 pt-3 border-t border-gray-200 flex flex-col gap-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <LevelBadge level={points.level} showProgress />
                  <span className="text-sm text-gray-600">
                    <span className="font-semibold text-gray-900">{points.karma}</span>
                    {" "}
                    {t("karma_unit", { defaultValue: "karma" })}
                  </span>
                </div>
              </div>
              {points.recent.length > 0 && (
                <div className="mt-1">
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">
                    {t("recent_kudos", { defaultValue: "Recent kudos" })}
                  </div>
                  <ul className="flex flex-col gap-1.5 max-h-40 overflow-y-auto pr-1">
                    {points.recent.map((tx) => (
                      <li key={tx.id} className="flex items-start gap-2 text-sm">
                        <UserAvatar
                          companyId={companyId}
                          user={tx.fromUser}
                          size={20}
                          rounded="lg"
                        />
                        <div className="min-w-0 flex-1">
                          <span className="font-medium text-gray-800">{tx.fromUser.name}</span>
                          <span
                            className="ml-1 align-middle tracking-tight"
                            title={`${tx.amount}`}
                            aria-label={`${tx.amount} kudos`}
                          >
                            {"⚡".repeat(tx.amount)}
                          </span>
                          <span className="text-gray-500">: </span>
                          <span className="text-gray-700">{tx.reason}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {(profile.email || profile.companyPhone || profile.manager) && (
            <div className="w-full mt-3 pt-3 border-t border-gray-200 flex flex-col gap-2">
              {profile.email && (
                <div className="flex items-center justify-between text-sm gap-2">
                  <span className="text-gray-500 shrink-0">{t("Email")}</span>
                  <a
                    href={`mailto:${profile.email}`}
                    className="text-blue-600 hover:underline truncate"
                  >
                    {profile.email}
                  </a>
                </div>
              )}
              {profile.companyPhone && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{t("phone")}</span>
                  <a href={`tel:${profile.companyPhone}`} className="text-blue-600 hover:underline">
                    {profile.companyPhone}
                  </a>
                </div>
              )}
              {profile.manager && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{t("manager")}</span>
                  <div className="flex items-center gap-2">
                    <UserAvatar
                      companyId={companyId}
                      user={{
                        id: profile.manager.id,
                        name: profile.manager.name,
                        avatarFileId: profile.manager.avatarFileId,
                      }}
                      size={24}
                      rounded="lg"
                    />
                    <span className="text-gray-900">{profile.manager.name}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="w-full mt-4 flex justify-center gap-2 flex-wrap">
            {isSelf ? (
              <Button size={isPhone ? "large" : undefined} onClick={handleOpenFullProfile}>
                {t("open_full_profile")}
              </Button>
            ) : (
              <>
                <Button size={isPhone ? "large" : undefined} variant="primary" onClick={handleSendMessage} loading={openingDm}>
                  {t("send_message")}
                </Button>
                <Button size={isPhone ? "large" : undefined} onClick={handleGiveKudos}>
                  {t("give_kudos", { defaultValue: "Give kudos" })}
                </Button>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UserPublicProfileModal;
